/**
 * Sample simulator contexts per seed policy.
 *
 * Each entry gives the operator a realistic starting point — enough to
 * hit an ALLOW outcome by default. The operator can then tweak the JSON
 * to trigger DENY scenarios and observe the trace panel.
 */

type SamplePair = { allow: Record<string, unknown>; deny: Record<string, unknown> };

export const SIMULATOR_SAMPLES: Record<string, SamplePair> = {
  'POL-VET-001': {
    allow: {
      profile: {
        compliance: {
          requiredCertifications: ['vet_license'],
        },
      },
      vetLicense: {
        expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180).toISOString(),
        verified: true,
      },
    },
    deny: {
      profile: {
        compliance: {
          requiredCertifications: ['vet_license'],
        },
      },
      vetLicense: {
        expiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
        verified: true,
      },
    },
  },
  'POL-CUSTOMER-BLACKLIST': {
    allow: {
      customer: { blacklistStatus: 'not_listed' },
    },
    deny: {
      customer: { blacklistStatus: 'chargeback_offender' },
    },
  },
  'POL-GDPR-RETENTION': {
    allow: {
      customer: {
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 365 * 2).toISOString(),
      },
      profile: { compliance: { dataRetentionDays: 1825 } },
    },
    deny: {
      customer: {
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 365 * 6).toISOString(),
      },
      profile: { compliance: { dataRetentionDays: 1825 } },
    },
  },
  'POL-TENANT-TIER': {
    allow: {
      tenant: { tier: 'pro' },
      module: { minTier: 'pro' },
    },
    deny: {
      tenant: { tier: 'basic' },
      module: { minTier: 'enterprise' },
    },
  },
  'POL-CANCEL-WINDOW': {
    allow: {
      booking: {
        startAt: new Date(Date.now() + 1000 * 60 * 60 * 48).toISOString(),
      },
      profile: { settings: { cancellationWindowHours: 24 } },
    },
    deny: {
      booking: {
        startAt: new Date(Date.now() + 1000 * 60 * 60 * 2).toISOString(),
      },
      profile: { settings: { cancellationWindowHours: 24 } },
    },
  },
  'POL-MAX-DAILY-BOOKINGS': {
    allow: {
      today: { bookingCount: 8 },
      profile: { scheduling: { maxDailyBookings: 20 } },
    },
    deny: {
      today: { bookingCount: 25 },
      profile: { scheduling: { maxDailyBookings: 20 } },
    },
  },
  'POL-PRESCRIPTION-REPEAT': {
    allow: {
      prescription: { sameDrugCount30d: 0 },
    },
    deny: {
      prescription: { sameDrugCount30d: 3 },
    },
  },
  'POL-FINANCIAL-LIMIT': {
    allow: {
      payouts: { todayTotal: 12_500 },
      tenant: { dailyPayoutLimit: 50_000 },
    },
    deny: {
      payouts: { todayTotal: 85_000 },
      tenant: { dailyPayoutLimit: 50_000 },
    },
  },
};

export function getSampleContext(policyId: string, mode: 'allow' | 'deny' = 'allow'): string {
  const pair = SIMULATOR_SAMPLES[policyId];
  if (!pair) {
    return JSON.stringify({ note: 'Provide a context object for this policy.' }, null, 2);
  }
  return JSON.stringify(pair[mode], null, 2);
}
